import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * The planner's worksheet: which pipeline lots have already been turned into
 * SIGM projects, ticked off one by one as the projects are created.
 *
 * Purely local. The mark lives in this browser's localStorage, for this user,
 * and nowhere else — the pipeline itself learns that a project exists the
 * next time it recomputes and drops the lot, so nothing here is a system of
 * record. It only has to outlive a page refresh and a coffee break, which the
 * in-memory selection the grid already has does not.
 *
 * Keyed by lot id. Lots keep their id across recomputes while the product
 * still qualifies, and a lot that leaves the pipeline takes its tick with it
 * (`prune`), so the count stays honest against what is on screen.
 */

const KEY = "nexpim.pipeline.worksheet";
/** "1" while checklist mode is on. Off is the default, and off means no ticks. */
const MODE_KEY = "nexpim.pipeline.worksheet.mode";

/** Lot id → when it was ticked, ISO-8601. */
type Ticks = Record<string, string>;

function read(): Ticks {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      ),
    );
  } catch {
    // Private windows and blocked site data both throw on read; a corrupt
    // value is a clean slate rather than a broken page.
    return {};
  }
}

function readMode(): boolean {
  try {
    return localStorage.getItem(MODE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(MODE_KEY, "1");
    else localStorage.removeItem(MODE_KEY);
  } catch {
    // Same as the ticks: the mode still works for this session.
  }
}

function write(ticks: Ticks): void {
  try {
    if (Object.keys(ticks).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(ticks));
  } catch {
    // A worksheet that cannot be remembered still works for this session.
  }
}

export interface PipelineWorksheet {
  /**
   * Checklist mode. Off is the plain pipeline; on adds the tick column and
   * the clear button. Turning it off empties the sheet — the mode is the
   * worksheet, there is no ticked-but-hidden state to wonder about later.
   */
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  /** Ticked lots among the ones supplied. */
  count: number;
  isDone: (lotId: number) => boolean;
  /** When the lot was ticked, or undefined. */
  doneAt: (lotId: number) => Date | undefined;
  toggle: (lotId: number) => void;
  clear: () => void;
}

/**
 * @param lotIds the lots currently in the pipeline; undefined while loading.
 *   Ticks for lots no longer listed are dropped once the list is known.
 */
export function usePipelineWorksheet(lotIds: readonly number[] | undefined): PipelineWorksheet {
  const [enabled, setEnabledState] = useState<boolean>(readMode);
  const [ticks, setTicks] = useState<Ticks>(read);

  useEffect(() => writeMode(enabled), [enabled]);
  useEffect(() => write(ticks), [ticks]);

  // Another tab ticking a lot should show up here without a reload: the
  // planner tends to have the pipeline open beside SIGM, sometimes twice.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === KEY) setTicks(read());
      if (event.key === null || event.key === MODE_KEY) setEnabledState(readMode());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Prune. Only once the pipeline has actually loaded — an empty list while
  // the query is in flight must not wipe the sheet.
  useEffect(() => {
    if (!lotIds || lotIds.length === 0) return;
    const live = new Set(lotIds.map(String));
    setTicks((previous) => {
      const kept = Object.entries(previous).filter(([id]) => live.has(id));
      return kept.length === Object.keys(previous).length ? previous : Object.fromEntries(kept);
    });
  }, [lotIds]);

  const count = useMemo(
    () => (lotIds ?? []).filter((id) => String(id) in ticks).length,
    [lotIds, ticks],
  );

  const isDone = useCallback((lotId: number) => String(lotId) in ticks, [ticks]);
  const doneAt = useCallback(
    (lotId: number) => {
      const at = ticks[String(lotId)];
      return at ? new Date(at) : undefined;
    },
    [ticks],
  );
  const toggle = useCallback((lotId: number) => {
    setTicks((previous) => {
      const key = String(lotId);
      const next = { ...previous };
      if (key in next) delete next[key];
      else next[key] = new Date().toISOString();
      return next;
    });
  }, []);
  const clear = useCallback(() => setTicks({}), []);
  const setEnabled = useCallback((on: boolean) => {
    setEnabledState(on);
    if (!on) setTicks({});
  }, []);

  // One identity per state, so the grid's column memo only rebuilds on a tick.
  return useMemo(
    () => ({ enabled, setEnabled, count, isDone, doneAt, toggle, clear }),
    [enabled, setEnabled, count, isDone, doneAt, toggle, clear],
  );
}
